import { createContext, useMemo, useEffect, useState, useCallback, useRef } from 'react';
import debounce from 'lodash/debounce';

import { useMutation } from '@apollo/client';
import * as Sentry from '@sentry/react';
import useQuery from '@/utilities/useQuery';
import { createOrderJob } from '@/components/ShipmentForm/queries/createOrderJob';
import { useLazyQuery } from '@apollo/client';
import { captureException } from '@sentry/react';
import { useNavigate } from 'react-router';
import { useClientUser, useReturnOrderFlow } from '@/hooks';

import { useAdminExceptionHooks } from './hooks';
import { GET_EXCEPTIONS, RESOLVE_EXCEPTION, GET_CARRIERS, GET_SHIPPERS } from './graphql';
import { exportCsv } from './queries/exportCsv';
import { COLUMNS, FILTERABLE_COLUMN_IDS } from './columns';
import { UPDATE_ORDER_BY_ID } from '@/graphql/mutations/orders';

const DEFAULT_SORT_BY = { id: 'exception_time', desc: true };

export const Context = createContext();

export const ContextProvider = ({ children }) => {
    const navigate = useNavigate();
    const { user_id } = useClientUser();
    const [toResolve, setToResolve] = useState({});
    const [selected, setSelected] = useState({});
    const [hasMore, setHasMore] = useState(false);
    const [sortBy, setSortBy] = useState(DEFAULT_SORT_BY);
    const [filter, setFilter] = useState({
        status: 'OPEN',
        shippers: [],
        carriers: [],
        searchTerm: '',
    });
    const skipNextFilterRefetch = useRef(true);

    const [getExceptions, { data, loading: initInflight, fetchMore }] = useLazyQuery(GET_EXCEPTIONS, {
        onError: (err) => {
            console.error(err);
            captureException(err);
        },
    });

    const [getShippers, { data: shipperData }] = useLazyQuery(GET_SHIPPERS, {
        onError: (err) => {
            captureException(err);
        },
    });

    const [getCarriers, { data: carrierData }] = useLazyQuery(GET_CARRIERS, {
        onError: (err) => {
            captureException(err);
        },
    });

    const [updateOrder] = useMutation(UPDATE_ORDER_BY_ID, {
        onError: (err) => {
            captureException(err);
        },
    });

    const exceptions = useMemo(() => {
        return data?.results || [];
    }, [data]);

    const shippers = useMemo(() => {
        return (
            (shipperData?.results || [])
                .filter((order) => !!order.shipper_id)
                .map((order) => {
                    return {
                        label: order?.order_shipper?.business_name,
                        value: order.shipper_id,
                    };
                })
                .sort((l, r) => l.label.localeCompare(r.label)) || []
        );
    }, [shipperData]);

    const carriers = useMemo(() => {
        return (
            (carrierData?.results || [])
                .filter((order) => !!order.carrier_id)
                .map((order) => {
                    return {
                        label: order?.order_carrier?.business_name,
                        value: order.carrier_id,
                    };
                })
                .sort((l, r) => l.label.localeCompare(r.label)) || []
        );
    }, [carrierData]);

    const where = useMemo(() => {
        return [
            ...(filter.start ? [{ created_at: { _gte: filter.start.toISOString() } }] : []),
            ...(filter.end ? [{ created_at: { _lt: filter.end.toISOString() } }] : []),
            ...(filter.occurred_at ? [{ exception: { reported_at: { _eq: filter.occurred_at } } }] : []),
            ...(filter.type ? [{ exception: { type: { _eq: filter.type } } }] : []),
            ...(filter?.shippers?.length > 0 ? [{ order: { shipper_id: { _in: filter?.shippers } } }] : []),
            ...(filter?.carriers?.length > 0 ? [{ order: { carrier_id: { _in: filter?.carriers } } }] : []),
            { order: { oms: { _eq: false } } },
            ...(filter?.searchTerm?.length
                ? [
                      {
                          _or: [
                              { order: { order_number: { _ilike: `%${filter?.searchTerm}%` } } },
                              { order: { po_number: { _ilike: `%${filter?.searchTerm}%` } } },
                          ],
                      },
                  ]
                : []),
        ];
    }, [filter]);

    const order_by = useMemo(() => {
        const ascOrDesc = sortBy?.desc ? 'desc_nulls_last' : 'asc_nulls_last';
        return [{ created_at: ascOrDesc }, { exception_type: 'desc_nulls_last' }];
    }, [sortBy]);

    const filtered = useMemo(() => {
        return Object.values(
            exceptions
                .filter((exception) => {
                    const resolved = exception.exception.status === 'RESOLVED';

                    return (!filter.type || exception.exception.type === filter.type) &&
                        (!filter.occurred_at || exception.exception.reported_at === filter.occurred_at) &&
                        filter.status === 'OPEN'
                        ? !resolved
                        : resolved &&
                              (filter.shippers.length === 0 || filter.shippers.includes(exception.order.shipper_id)) &&
                              (filter.carriers.length === 0 || filter.carriers.includes(exception.order.carrier_id));
                })
                .reduce((acc, exception) => {
                    acc[exception.exception.exception_id] = {
                        ...exception,
                        ...(exception.item
                            ? { item: [...((acc[exception.exception.exception_id] || {}).item || []), exception.item] }
                            : {}),
                    };

                    return acc;
                }, {})
        );
    }, [exceptions, filter]);

    const [retrieveCsv, { loading: csvInflight }] = useQuery(exportCsv, {
        onError: (err) => {
            Sentry.captureException(err);
        },
    });

    const { exportCSV, loadMore } = useAdminExceptionHooks(
        { where, exceptions, order_by },
        { fetchMore, setHasMore, retrieveCsv }
    );

    const getExceptionsDebounced = useMemo(
        () =>
            debounce((payload) => {
                return getExceptions(payload);
            }, 500),
        []
    );

    useEffect(() => {
        return () => {
            getExceptionsDebounced.cancel();
        };
    }, [getExceptionsDebounced]);

    useEffect(() => {
        setHasMore(true);
        getExceptions({
            variables: {
                where: { _and: where },
                order_by,
            },
        });

        getShippers({
            variables: {
                where: {
                    _and: [
                        ...(filter.start ? [{ created_at: { _gte: filter.start.toISOString() } }] : []),
                        ...(filter.end ? [{ created_at: { _lt: filter.end.toISOString() } }] : []),
                    ],
                },
            },
        });

        getCarriers({
            variables: {
                where: {
                    _and: [
                        ...(filter.start ? [{ created_at: { _gte: filter.start.toISOString() } }] : []),
                        ...(filter.end ? [{ created_at: { _lt: filter.end.toISOString() } }] : []),
                    ],
                },
            },
        });
    }, []);

    useEffect(() => {
        if (skipNextFilterRefetch.current) {
            skipNextFilterRefetch.current = false;
            return;
        }

        getExceptionsDebounced({
            variables: {
                where: { _and: where },
                order_by,
            },
        });
        setHasMore(true);
    }, [where, order_by, getExceptionsDebounced]);

    const [submitOrders, { loading: submitInflight }] = useQuery(createOrderJob, {
        onError: (err) => {
            Sentry.captureException(err);
        },
    });

    const [resolve, { loading: resolveInflight }] = useMutation(RESOLVE_EXCEPTION, {
        update(cache, { data: { order, exception, items_removed } }) {
            cache.updateQuery(
                {
                    query: GET_EXCEPTIONS,
                    variables: {
                        where: { _and: where },
                        order_by,
                    },
                },
                (data) => {
                    const removed = Object.fromEntries(items_removed.returning.map((item) => [item.item_id, true]));
                    const [resolved] = exception.returning;
                    const [updated] = order.returning;

                    const clone = [...data.results].map((mapping) => {
                        return {
                            ...mapping,
                            ...(mapping.exception.exception_id === resolved.exception_id
                                ? {
                                      order: updated ? updated : mapping.order,
                                      exception: {
                                          ...mapping.exception,
                                          status: resolved.status,
                                      },
                                  }
                                : {}),
                        };
                    });

                    return { results: clone.filter((item) => !removed[item.item_id]) };
                }
            );
        },
        onError: (err) => {
            console.log(err);
            Sentry.captureException(err);
        },
    });

    const saveAssociatedReturnOrder = useReturnOrderFlow({
        setReturnOrderId: (oid, rid) => updateOrder({ variables: { order_id: oid, update: { return_order_id: rid } } }),
    });

    const callbacks = {
        resolveException: (row) => {
            const mapping = row?.original ?? row;
            setToResolve({ exception: mapping.exception, items: mapping.item, order: mapping.order });
        },
        closeModal: () => {
            setToResolve({});
        },
        selectExceptions: setSelected,
        exportCSV,
        setFilter,
        setSortBy,
        loadMore,
        submitOrders,
        resolve,
        saveAssociatedReturnOrder,
        gotoDetails: (order) => navigate(`/order/${order?.order_id}`),
        user_id,
    };

    return (
        <Context.Provider
            value={{
                state: {
                    toResolve,
                    filter,
                    hasMore,
                    exceptions: filtered,
                    selected,
                    sortBy,
                    columns: COLUMNS,
                    filterableColumnIds: FILTERABLE_COLUMN_IDS,
                    carriers,
                    shippers,
                    tableType: 'ADMIN_EXCEPTIONS',
                },
                loading: {
                    init: initInflight,
                    resolve: resolveInflight || submitInflight,
                    export: csvInflight,
                },
                callbacks,
            }}
        >
            {children}
        </Context.Provider>
    );
};
